import {
	FeedCompaniesResponse,
	FeedCountsResponse,
	FeedIgnoreResponse,
	FeedLeadScoreFilter,
	FeedPageResponse,
	FeedSignalScoreFilter,
	FeedSort,
	FeedTab,
	feedCompaniesResponseSchema,
	feedCountsResponseSchema,
	feedIgnoreResponseSchema,
	feedPageResponseSchema,
} from "@signalist/shared-types";

import { api } from "@/redux/api";

import type { IFilterWithValues } from "@/pages/Private/pages/List/components/FilterModal";

/** Pager page size of the Feed page (also the `limit` of the jump-to-company endpoint). */
export const FEED_PAGE_SIZE = 10;

/** Filters shared by all three feed endpoints, taken from the feed slice. */
export interface FeedFilterArgs {
	/** Selected tracker-list ids (numbers; serialized as a JSON number array). */
	listIds: number[];
	search: string;
	/** Lead-score filter (PriorityFilterStatus values); empty = all leads. */
	priorityFilterStatus: FeedLeadScoreFilter[];
	/** Signal-score filter (SignalFilterStatus values); empty = all signals. */
	signalFilterStatus: FeedSignalScoreFilter[];
	/** Funnel-modal filter (list-page FilterModal); null = no filter. */
	filter: IFilterWithValues | null;
}

export interface FeedPageArgs extends FeedFilterArgs {
	tab: FeedTab;
	page: number;
	sort: FeedSort;
}

export type FeedCompaniesArgs = Omit<FeedPageArgs, "page">;

/** Args of the run ignore/undo mutations — the currently selected lists (D3). */
export interface FeedIgnoreArgs {
	runId: number;
	listIds: number[];
}

/** A score filter is "real" when it narrows anything — empty or ALL does not. */
const isRealScoreFilter = (statuses: string[]): boolean =>
	statuses.length > 0 && !statuses.includes("ALL");

/**
 * Maps the shared filter state to wire params. `listIds` and the score filters
 * go out as JSON arrays; defaults (empty search, empty/ALL score filters) are
 * omitted so the request carries only real filters.
 */
export const buildFeedFilterParams = ({
	listIds,
	search,
	priorityFilterStatus,
	signalFilterStatus,
	filter,
}: FeedFilterArgs): Record<string, string> => ({
	listIds: JSON.stringify(listIds.map(Number)),
	...(search && { search }),
	...(isRealScoreFilter(priorityFilterStatus) && {
		priorityFilterStatus: JSON.stringify(priorityFilterStatus),
	}),
	...(isRealScoreFilter(signalFilterStatus) && {
		signalFilterStatus: JSON.stringify(signalFilterStatus),
	}),
	...(filter && Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
});

export const buildFeedPageParams = ({
	tab,
	page,
	sort,
	...filters
}: FeedPageArgs): Record<string, string | number> => ({
	...buildFeedFilterParams(filters),
	tab,
	page,
	limit: FEED_PAGE_SIZE,
	sort,
});

export const buildFeedCompaniesParams = ({
	tab,
	sort,
	...filters
}: FeedCompaniesArgs): Record<string, string | number> => ({
	...buildFeedFilterParams(filters),
	tab,
	sort,
	limit: FEED_PAGE_SIZE,
});

export const feedApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getFeed: builder.query<FeedPageResponse, FeedPageArgs>({
			query: (args) => ({
				url: "feed",
				params: buildFeedPageParams(args),
				responseSchema: feedPageResponseSchema,
			}),
			providesTags: ["Feed"],
		}),
		getFeedCounts: builder.query<FeedCountsResponse, FeedFilterArgs>({
			query: (args) => ({
				url: "feed/counts",
				params: buildFeedFilterParams(args),
				responseSchema: feedCountsResponseSchema,
			}),
			providesTags: ["FeedCounts"],
		}),
		getFeedCompanies: builder.query<FeedCompaniesResponse, FeedCompaniesArgs>({
			query: (args) => ({
				url: "feed/companies",
				params: buildFeedCompaniesParams(args),
				responseSchema: feedCompaniesResponseSchema,
			}),
			providesTags: ["Feed"],
		}),
		/**
		 * Ignore-this-time (D3). Deliberately does NOT invalidate `Feed` — the slice
		 * removes the card and applies the response counts, keeping the pager position.
		 */
		ignoreFeedRun: builder.mutation<FeedIgnoreResponse, FeedIgnoreArgs>({
			query: ({ runId, listIds }) => ({
				url: `feed/runs/${runId}/ignore`,
				method: "POST",
				body: { listIds },
				responseSchema: feedIgnoreResponseSchema,
			}),
		}),
		/**
		 * Undo of the ignore (D3). Re-inserting the card locally is not attempted:
		 * invalidating `Feed` refetches the page consistently; counts come from the
		 * response via the slice matcher.
		 */
		undoIgnoreFeedRun: builder.mutation<FeedIgnoreResponse, FeedIgnoreArgs>({
			query: ({ runId, listIds }) => ({
				url: `feed/runs/${runId}/ignore`,
				method: "DELETE",
				body: { listIds },
				responseSchema: feedIgnoreResponseSchema,
			}),
			invalidatesTags: ["Feed"],
		}),
	}),
});

export const {
	useGetFeedQuery,
	useGetFeedCountsQuery,
	useGetFeedCompaniesQuery,
	useLazyGetFeedCompaniesQuery,
	useIgnoreFeedRunMutation,
	useUndoIgnoreFeedRunMutation,
} = feedApi;
